import {
  useRef,
  type FC,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { Box, Portal } from "@mui/material";
import {
  DOCK_MIN_WIDTH,
  clampDockWidth,
  setDockResizing,
  setDockWidth,
  useDockWidth,
} from "../model/dockStore";

/**
 * Высота шапки кабинета (Toolbar от sm). Шапка видна только ниже lg, там
 * панель стоит под ней; от lg шапки нет, и панель на всю высоту окна.
 */
const HEADER_HEIGHT = 64;
/** Шаг изменения ширины стрелками с клавиатуры. */
const KEY_STEP = 16;

export interface AssistantDockProps {
  id: string;
  onClose: () => void;
  children: ReactNode;
}

/**
 * Боковая панель помощника на десктопе, как чат в редакторе кода: стоит
 * справа на всю высоту окна (ниже lg под шапкой), страница сужается на её ширину (отступ
 * задаёт Layout), фона и ловушки фокуса нет. Левый край тянется мышью
 * или стрелками. Слой ниже шапки, ящика меню и любых модальных окон
 * кабинета: диалоги открываются поверх панели.
 */
export const AssistantDock: FC<AssistantDockProps> = ({
  id,
  onClose,
  children,
}) => {
  const { t } = useTranslation();
  const width = useDockWidth();
  const drag = useRef<{
    startX: number;
    startWidth: number;
    last: number;
  } | null>(null);
  const maxWidth = clampDockWidth(Number.MAX_SAFE_INTEGER, window.innerWidth);

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { startX: e.clientX, startWidth: width, last: width };
    setDockResizing(true);
    document.body.style.userSelect = "none";
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    const next = clampDockWidth(
      drag.current.startWidth + (drag.current.startX - e.clientX),
      window.innerWidth
    );
    drag.current.last = next;
    setDockWidth(next, false);
  };

  const endDrag = (e: PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    const { last } = drag.current;
    drag.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    document.body.style.userSelect = "";
    setDockResizing(false);
    setDockWidth(last, true);
  };

  const onHandleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const delta =
      e.key === "ArrowLeft" ? KEY_STEP : e.key === "ArrowRight" ? -KEY_STEP : 0;
    if (!delta) return;
    e.preventDefault();
    setDockWidth(clampDockWidth(width + delta, window.innerWidth), true);
  };

  // Esc закрывает панель, только если фокус в ней самой: события из
  // порталов (меню, диалоги) всплывают по дереву React, их пропускаем.
  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key !== "Escape" || e.defaultPrevented) return;
    if (!e.currentTarget.contains(e.target as Node)) return;
    e.preventDefault();
    onClose();
  };

  return (
    <Portal>
      <Box
        component="aside"
        id={id}
        role="complementary"
        aria-label={t("partnerAssistant.title")}
        onKeyDown={onKeyDown}
        sx={({ palette, zIndex }) => ({
          position: "fixed",
          top: { xs: HEADER_HEIGHT, lg: 0 },
          right: 0,
          bottom: 0,
          width,
          // Ниже шапки (ниже lg её нижняя граница видна) и модальных окон.
          zIndex: zIndex.appBar - 1,
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.paper",
          color: "text.primary",
          borderLeft: `1px solid ${palette.divider}`,
        })}
      >
        <Box
          role="separator"
          aria-orientation="vertical"
          aria-label={t("partnerAssistant.resize")}
          aria-valuenow={width}
          aria-valuemin={DOCK_MIN_WIDTH}
          aria-valuemax={maxWidth}
          tabIndex={0}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onKeyDown={onHandleKeyDown}
          title={t("partnerAssistant.resize")}
          sx={({ palette }) => ({
            position: "absolute",
            top: 0,
            bottom: 0,
            left: -4,
            width: 8,
            cursor: "col-resize",
            zIndex: 1,
            touchAction: "none",
            outline: "none",
            "&::after": {
              content: '""',
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 3,
              width: 2,
              bgcolor: "transparent",
              transition: "background-color 150ms",
            },
            "&:hover::after, &:focus-visible::after": {
              bgcolor: palette.primary.main,
            },
          })}
        />
        <Box sx={{ flex: 1, minHeight: 0 }}>{children}</Box>
      </Box>
    </Portal>
  );
};
